import { useEffect, useState } from 'react'
import { toggleChecks } from '@/utils/acl'

import { IACL, OrgPermission, TeamMemberRole } from '@makyoapp/makyo-sdk/models/ACL'
import { CheckboxTableKey } from '@makyoapp/makyo-ui'

import { ACLValues, getTeamACL, rebuildTeamACL, TeamACLLabel } from '@/adapters/ACLAdapter'
import { useUpdateACLMutation } from '@/resolvers/org/mutation'
import { useCurrentOrgContext } from '@/context/CurrentOrgContext'
import { useOrgsContext } from '@/context/OrgsContext'
import { useReqError } from '@/hooks/useReqError'

/**
 * Hook that handles the logic related to the Access Control List (ACL) for teams.
 *
 * @param {IACL} rules - The initial ACL rules.
 * @param {(saved: boolean) => void} setIsSaved - Function to set the saved state.
 *
 * @returns {object} An object containing the current ACL values, a boolean flag indicating if the ACL can be edited,
 * and a function to handle checkbox changes.
 */
const useACLTeam = (rules: IACL, setIsSaved: (saved: boolean) => void) => {
  const printAPIError = useReqError()
  const [values, setValues] = useState<ACLValues<TeamACLLabel, TeamMemberRole>[]>(getTeamACL(rules._Team))

  const { orgsPerms } = useOrgsContext()
  const { currentOrg } = useCurrentOrgContext()
  // Determine if the current user has the permission to make ACL changes
  const canEditACL = orgsPerms[currentOrg?.id || '']?.[OrgPermission.ACLMakeChanges]
  // Define the mutation for updating the ACL
  const { mutate: updateAclMutation } = useUpdateACLMutation()

  // Listen rules data to update checklist table
  useEffect(() => {
    setValues(getTeamACL(rules._Team))
  }, [rules])

  // Function to update the ACL values when a scope is toggled
  const toggleScope = (nextValues: ACLValues<TeamACLLabel, TeamMemberRole>[]) => {
    // Update the local state with the new ACL values
    setValues(nextValues)
    // Reconstruct the ACL for the team with the updated values
    const teamACL = rebuildTeamACL(nextValues, rules._Team)

    // Execute the mutation to update the ACL on the server
    updateAclMutation(
      { org: rules._Organization, team: teamACL },
      {
        // On successful mutation, indicate that changes have been saved
        onSuccess() {
          setIsSaved(true)
        },
        // On mutation error, print the error
        onError(e) {
          printAPIError(e)
        },
      }
    )
  }

  // Function to handle checkbox changes in the ACL
  const handleCheck = (scopeIdx: number, check: string, key: CheckboxTableKey<TeamMemberRole>) => {
    // If the user does not have permission to edit the ACL, exit the function
    if (!canEditACL) {
      return
    }
    // Update the checks for the corresponding scope, and trigger an update to the ACL
    toggleChecks(scopeIdx, check, key.id, values, toggleScope)
  }

  return { values, canEditACL, handleCheck }
}

export default useACLTeam
